import { useState } from "react";
import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import BiblePassageInput from "../sundayservice/BiblePassageInput";
import { BIBLE_BOOKS, assembleBiblePassage, parseBiblePassage } from "../utils/bibleBooks";

export type MyPlanMode = "sermon" | "intro" | "reminder" | "choir";

/** One of the preacher's own slots, as sent by the getMyPlan callable. */
export interface MyPlanService {
    serviceId: string;
    date: string;
    serviceType: string;
    serviceLabel: string;
    slot: 1 | 2;
    mode: MyPlanMode;
    topic: string;
    biblePassage: string;
    prayerConcern: string;
    confirmedAt: string | null;
    status: "open" | "done" | "declined" | "swap-pending";
    // Display name of the colleague asked to take the slot (status "swap-pending").
    swapWith: string;
    otherPreacherName: string;
}

export interface MyPlanSlotInput {
    topic: string;
    biblePassage: string;
    prayerConcern: string;
}

interface MyPlanSlotFormProps {
    service: MyPlanService;
    onSubmit: (input: MyPlanSlotInput) => Promise<void>;
}

/** The form for one slot: sermon details, prayer concerns or a plain confirmation. */
export default function MyPlanSlotForm({ service, onSubmit }: MyPlanSlotFormProps) {
    const { t } = useTranslation();
    const parsed = parseBiblePassage(service.biblePassage || "");
    const knownBook = parsed.book && BIBLE_BOOKS.includes(parsed.book);

    const [topic, setTopic] = useState(service.topic);
    const [prayerConcern, setPrayerConcern] = useState(service.prayerConcern);
    const [bookInput, setBookInput] = useState(knownBook ? parsed.book : "");
    const [selectedBook, setSelectedBook] = useState(knownBook ? parsed.book : "");
    const [chapter, setChapter] = useState(knownBook ? parsed.chapter : "");
    const [verses, setVerses] = useState(knownBook ? parsed.verses : "");
    const [chapterError, setChapterError] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const idSuffix = `-${service.serviceId}-${service.slot}`;
    // A passage that the structured input cannot represent is kept as it is
    // unless the preacher picks a new one.
    const biblePassage = selectedBook
        ? assembleBiblePassage(selectedBook, chapter, verses)
        : knownBook
            ? ""
            : service.biblePassage;

    const canSubmit =
        service.mode === "sermon"
            ? (!!topic.trim() || !!biblePassage) && !(selectedBook && chapterError)
            : service.mode === "intro"
                ? !!prayerConcern.trim()
                : true;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        setError("");
        try {
            await onSubmit({ topic: topic.trim(), biblePassage, prayerConcern: prayerConcern.trim() });
        } catch {
            setError(t("myPlan.saveFailed") as string);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            {service.mode === "sermon" && (
                <>
                    <Form.Group className="mb-3" controlId={`topic${idSuffix}`}>
                        <Form.Label>{t("sermonInfo.topic")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={topic}
                            maxLength={500}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder={t("sermonInfo.topicPlaceholder") as string}
                        />
                    </Form.Group>
                    <Form.Label>{t("sermonInfo.biblePassage")}</Form.Label>
                    {!knownBook && service.biblePassage && (
                        <Form.Text className="d-block mb-2">{t("myPlan.currentPassage", { passage: service.biblePassage })}</Form.Text>
                    )}
                    <BiblePassageInput
                        bookInput={bookInput}
                        selectedBook={selectedBook}
                        chapter={chapter}
                        verses={verses}
                        chapterError={chapterError}
                        onBookChange={(input, book) => {
                            setBookInput(input);
                            setSelectedBook(book);
                        }}
                        onChapterChange={setChapter}
                        onVersesChange={setVerses}
                        onChapterErrorChange={setChapterError}
                        idSuffix={idSuffix}
                    />
                </>
            )}
            {service.mode === "intro" && (
                <Form.Group className="mb-3" controlId={`prayer${idSuffix}`}>
                    <Form.Label>{t("sermonInfo.prayerConcern")}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={prayerConcern}
                        maxLength={4000}
                        onChange={(e) => setPrayerConcern(e.target.value)}
                    />
                </Form.Group>
            )}
            {service.mode === "reminder" && <p className="mb-3">{t("myPlan.confirmHint")}</p>}

            {error && <Alert variant="danger">{error}</Alert>}
            <Button type="submit" disabled={saving || !canSubmit} className="w-100 mt-2">
                {saving && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                {service.mode === "reminder" ? t("myPlan.confirm") : t("admin.save")}
            </Button>
        </Form>
    );
}
